import { computed, makeObservable } from 'mobx'
import type moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'
import { Model } from 'modules/core/lib/mobx-rest'

import shifts from '../collections/shifts'
import type Shift from './Shift'

interface IEditTimesheetRequest {
  id: number
  attendance_shift_id: number
  employee_id: number
  request_type: string
  date: string
  clock_in: string
  clock_out: string
  location_type?: string
  observations?: string
  approved: boolean
  time_settings_break_configuration_id: number
  workable: boolean
}

export const RequestTypes = {
  createShift: 'create_shift',
  updateShift: 'update_shift',
  deleteShift: 'delete_shift',
} as const

export type RequestType = (typeof RequestTypes)[keyof typeof RequestTypes]

class EditTimesheetRequest extends Model<IEditTimesheetRequest> {
  constructor(attr: IEditTimesheetRequest) {
    super(attr)
    makeObservable(this, {
      isCreateShift: computed,
      date: computed,
      timeSettingsBreakId: computed,
    })
  }

  static urlRoot() {
    return '/api/rest/attendance/editTimesheetRequest'
  }

  get isCreateShift(): boolean {
    return this.attributes.request_type === RequestTypes.createShift
  }

  get isUpdateShift(): boolean {
    return this.attributes.request_type === RequestTypes.updateShift
  }

  get isDeleteShift(): boolean {
    return this.attributes.request_type === RequestTypes.deleteShift
  }

  get timeSettingsBreakId(): string | undefined {
    if (this.attributes.time_settings_break_configuration_id == undefined) {
      return
    }

    return this.attributes.time_settings_break_configuration_id.toString()
  }

  get attendanceShift(): Shift | undefined {
    if (this.isCreateShift) return undefined
    return shifts.find({ id: this.attributes.attendance_shift_id })
  }

  get date(): moment.Moment {
    if (!this.attributes.date) {
      return this.attendanceShift!.date
    }
    return stringToDate(this.attributes.date, 'backend')
  }

  get isPending(): boolean {
    return this.attributes.approved == null
  }
}
